import { Injectable } from '@angular/core';
import { NavigationItemInterface } from 'app/layout/components/navigation/models/navigation-item-interface';
import merge from 'lodash-es/merge';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FuseNavigationService {
  public onItemCollapsed: Subject<NavigationItemInterface>;
  public onItemCollapseToggled: Subject<void>;

  // Private
  private _currentNavigationKey: string | null;
  private _onNavigationChanged: BehaviorSubject<string | null>;
  private _onNavigationItemAdded: BehaviorSubject<boolean | null>;
  private _onNavigationItemRemoved: BehaviorSubject<boolean | null>;
  private _onNavigationItemUpdated: BehaviorSubject<boolean | null>;
  private _onNavigationRegistered: BehaviorSubject<[string, NavigationItemInterface[]] | null>;
  private _onNavigationUnregistered: BehaviorSubject<string | null>;
  private _registry: { [key: string]: NavigationItemInterface[] } = {};

  /**
   * Constructor
   */
  public constructor() {
    // Set the defaults
    this.onItemCollapsed = new Subject<NavigationItemInterface>();
    this.onItemCollapseToggled = new Subject<void>();

    // Set the private defaults
    this._currentNavigationKey = null;
    this._onNavigationChanged = new BehaviorSubject(null);
    this._onNavigationRegistered = new BehaviorSubject(null);
    this._onNavigationUnregistered = new BehaviorSubject(null);
    this._onNavigationItemAdded = new BehaviorSubject(null);
    this._onNavigationItemUpdated = new BehaviorSubject(null);
    this._onNavigationItemRemoved = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get onNavigationChanged
   *
   * @returns {Observable<string | null>}
   */
  public get onNavigationChanged(): Observable<string | null> {
    return this._onNavigationChanged.asObservable();
  }

  /**
   * Get onNavigationItemAdded
   *
   * @returns {Observable<boolean | null>}
   */
  public get onNavigationItemAdded(): Observable<boolean | null> {
    return this._onNavigationItemAdded.asObservable();
  }

  /**
   * Get onNavigationItemRemoved
   *
   * @returns {Observable<boolean | null>}
   */
  public get onNavigationItemRemoved(): Observable<boolean | null> {
    return this._onNavigationItemRemoved.asObservable();
  }

  /**
   * Get onNavigationItemUpdated
   *
   * @returns {Observable<boolean | null>}
   */
  public get onNavigationItemUpdated(): Observable<boolean | null> {
    return this._onNavigationItemUpdated.asObservable();
  }

  /**
   * Get onNavigationRegistered
   *
   * @returns {Observable<[string, NavigationItemInterface[]] | null>}
   */
  public get onNavigationRegistered(): Observable<[string, NavigationItemInterface[]] | null> {
    return this._onNavigationRegistered.asObservable();
  }

  /**
   * Get onNavigationUnregistered
   *
   * @returns {Observable<string | null>}
   */
  public get onNavigationUnregistered(): Observable<string | null> {
    return this._onNavigationUnregistered.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Add a navigation item to the specified location
   *
   * @param item
   * @param id
   */
  public addNavigationItem(item: NavigationItemInterface, id: string): void {
    // Get the current navigation
    const navigation: NavigationItemInterface[] | undefined = this.getCurrentNavigation();

    // Add to the end of the navigation
    if (id === 'end') {
      navigation!.push(item);

      // Trigger the observable
      this._onNavigationItemAdded.next(true);

      return;
    }

    // Add to the start of the navigation
    if (id === 'start') {
      navigation!.unshift(item);

      // Trigger the observable
      this._onNavigationItemAdded.next(true);

      return;
    }

    // Add it to a specific location
    const parent: NavigationItemInterface | false = this.getNavigationItem(id);

    if (parent) {
      // Check if parent has a children entry,
      // and add it if it doesn't
      if (!parent.children) {
        parent.children = [];
      }

      // Add the item
      parent.children.push(item);
    }

    // Trigger the observable
    this._onNavigationItemAdded.next(true);
  }

  /**
   * Get the current navigation
   *
   * @returns {NavigationItemInterface[]}
   */
  public getCurrentNavigation(): NavigationItemInterface[] | undefined {
    if (!this._currentNavigationKey) {
      console.warn(`The current navigation is not set.`);

      return;
    }

    return this.getNavigation(this._currentNavigationKey);
  }

  /**
   * Get flattened navigation array
   *
   * @param navigation
   * @param flatNavigation
   * @returns {any[]}
   */
  public getFlatNavigation(
    navigation: NavigationItemInterface[] | undefined,
    flatNavigation: NavigationItemInterface[] = [],
  ): NavigationItemInterface[] {
    if (!navigation || !flatNavigation) {
      return [];
    }

    for (const item of navigation) {
      if (item.type === 'item') {
        // only menu items with url are supported as shortcut
        if (item.url) {
          flatNavigation.push(item);
        }

        continue;
      }

      if (item.type === 'collapsable' || item.type === 'group') {
        if (item.children) {
          this.getFlatNavigation(item.children, flatNavigation);
        }
      }
    }

    return flatNavigation;
  }

  /**
   * Get navigation from registry by key
   *
   * @param key
   * @returns {NavigationItemInterface[]}
   */
  public getNavigation(key: string): NavigationItemInterface[] | undefined {
    // Check if the navigation exists
    if (!this._registry[key]) {
      console.warn(`The navigation with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    // Return the sidebar
    return this._registry[key];
  }

  /**
   * Get navigation item by id from the
   * current navigation
   *
   * @param id
   * @param {any} navigation
   * @returns {any | boolean}
   */
  public getNavigationItem(id: string, navigation: NavigationItemInterface[] | null | undefined = null): NavigationItemInterface | false {
    if (!navigation) {
      navigation = this.getCurrentNavigation();
    }

    for (const item of navigation!) {
      if (item.id === id) {
        return item;
      }

      if (item.children) {
        const childItem = this.getNavigationItem(id, item.children);

        if (childItem) {
          return childItem;
        }
      }
    }

    return false;
  }

  /**
   * Get the parent of the navigation item
   * with the id
   *
   * @param id
   * @param {any} navigation
   * @param parent
   */
  public getNavigationItemParent(
    id: string,
    navigation: NavigationItemInterface[] | null | undefined = null,
    parent: NavigationItemInterface[] | NavigationItemInterface | null | undefined = null,
  ): NavigationItemInterface[] | NavigationItemInterface | false | null | undefined {
    if (!navigation) {
      navigation = this.getCurrentNavigation();
      parent = navigation;
    }

    for (const item of navigation!) {
      if (item.id === id) {
        return parent;
      }

      if (item.children) {
        const childItem = this.getNavigationItemParent(id, item.children, item);

        if (childItem) {
          return childItem;
        }
      }
    }

    return false;
  }

  /**
   * Register the given navigation
   * with the given key
   *
   * @param key
   * @param navigation
   */
  public register(key: string, navigation: NavigationItemInterface[]): void {
    // Check if the key already being used
    if (this._registry[key]) {
      console.error(`The navigation with the key '${key}' already exists. Either unregister it first or use a unique key.`);

      return;
    }

    // Add to the registry
    this._registry[key] = navigation;

    this._onNavigationRegistered.next([key, navigation]);
  }

  /**
   * Remove navigation item with the given id
   *
   * @param id
   */
  public removeNavigationItem(id: string): void {
    const item = this.getNavigationItem(id);

    // Return, if there is not such an item
    if (!item) {
      return;
    }

    // Get the parent of the item
    const parent = this.getNavigationItemParent(id);

    // This should never happen since we confirmed item exists above
    if (!parent) {
      return;
    }

    // This check is required because of the first level
    // of the navigation, since the first level is not
    // inside the 'children' array
    const parentArray: NavigationItemInterface[] = Array.isArray(parent) ? parent : (parent.children ?? []);

    // Remove the item
    parentArray.splice(parentArray.indexOf(item), 1);

    // Trigger the observable
    this._onNavigationItemRemoved.next(true);
  }

  /**
   * Set the navigation with the key
   * as the current navigation
   *
   * @param key
   */
  public setCurrentNavigation(key: string): void {
    // Check if the sidebar exists
    if (!this._registry[key]) {
      console.warn(`The navigation with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    // Set the current navigation key
    this._currentNavigationKey = key;

    this._onNavigationChanged.next(key);
  }

  /**
   * Unregister the navigation from the registry
   * @param key
   */
  public unregister(key: string): void {
    // Check if the navigation exists
    if (!this._registry[key]) {
      console.warn(`The navigation with the key '${key}' doesn't exist in the registry.`);
    }

    // Unregister the sidebar
    delete this._registry[key];

    this._onNavigationUnregistered.next(key);
  }

  /**
   * Update navigation item with the given id
   *
   * @param id
   * @param properties
   */
  public updateNavigationItem(id: string, properties: Partial<NavigationItemInterface>): void {
    // Get the navigation item
    const navigationItem = this.getNavigationItem(id);

    // If there is no navigation with the give id, return
    if (!navigationItem) {
      return;
    }

    // Merge the navigation properties
    merge(navigationItem, properties);

    // Trigger the observable
    this._onNavigationItemUpdated.next(true);
  }
}
